import { useAction, useAtom } from '@reatom/npm-react'
import styled from 'styled-components'

import { CurrencyIcon } from '@root/entities/currency/ui'
import { useTranslation } from '@root/shared/localization'
import { Input } from '@root/shared/ui-kit'

import { AmountInputNodeDefs } from '../../model/nodes'

import type { DistributeFundsModel } from '../../atoms'
import type { StyleProps } from '@root/shared/react'

type AmountInputNodeProps = {
  model: DistributeFundsModel
  ref?: React.Ref<HTMLDivElement>
} & StyleProps

export const AmountInputNode = ({
  model,
  style,
  ref,
}: AmountInputNodeProps) => {
  const { t } = useTranslation()
  const { amountToAllocate } = model
  const [wallet] = useAtom(model.sourceWallet)
  const [availableBalance] = useAtom(model.availableBalance)
  const [currency] = useAtom(model.currency)

  const [amountToAllocateValue] = useAtom(amountToAllocate)
  const [amountToAllocateRaw] = useAtom(amountToAllocate.amount.raw)
  const [percentageRaw] = useAtom(amountToAllocate.percentage.raw)
  const setRawAmount = useAction(amountToAllocate.setAmountRaw)
  const setPercentageAmount = useAction(amountToAllocate.setPercentageRaw)

  const isAmountValid = amountToAllocateValue.lte(availableBalance)
  return (
    <Root style={style} ref={ref}>
      <Input
        disabled={!currency || !wallet}
        startContent={<span>{t('common.amount')}</span>}
        value={amountToAllocateRaw}
        placeholder="0"
        size="h36"
        numeric
        error={!isAmountValid}
        onChange={(e) => setRawAmount(e.target.value)}
        endContent={
          currency ? (
            <CurrencyIcon
              currency={currency}
              size={24}
              withGoogleLens={false}
            />
          ) : undefined
        }
      />
      <Input
        size="h36"
        numeric
        value={percentageRaw}
        isAllowed={({ floatValue }) => {
          return floatValue === undefined || floatValue <= 100
        }}
        allowLeadingZeros={false}
        placeholder="0"
        onChange={(e) => {
          setPercentageAmount(e.target.value)
        }}
        endContent={<CharIcon>%</CharIcon>}
      />
    </Root>
  )
}

const Root = styled.div`
  display: grid;
  grid-template-columns: 254px minmax(0, 1fr);
  align-items: center;
  gap: 2px;

  border-radius: 12px;
  border: 1px solid var(--background-3);
  background: var(--background-1);
  padding-inline: 12px;

  width: ${AmountInputNodeDefs.width}px;
  height: ${AmountInputNodeDefs.height}px;
`

const CharIcon = styled.div`
  color: var(--text-11);
  text-align: right;
  font-size: 12px;
  font-weight: 600;
  line-height: 20px;
`
